import { NOTIFICATION_TYPES } from "../constants/constants";
import { AiResponseContentType, ContentSegment, ParsedContent } from "../models/chat_content";


export const getNotificationTypeText = (notifType)=>{
    if(notifType === NOTIFICATION_TYPES.AI_CHAT){
        return 'AI Chat';
    }
    if(notifType === NOTIFICATION_TYPES.ATTENDANCE){
        return 'Attendance';
    }
    if(notifType === NOTIFICATION_TYPES.NOTICE){
        return 'Notice';
    }
    if(notifType === NOTIFICATION_TYPES.TUTOR){
        return 'Tutor';
    }
    return 'Normal';
}

/**
 * Removes markdown code block syntax from a string.
 * @param {string} text The text to clean.
 * @returns {string} The cleaned text.
 */
function removeCodeSyntax(text) {
    const markers = ['```json', '```', '~~~json', '~~~'];
    let cleanedText = text;
    for (const marker of markers) {
        cleanedText = cleanedText.replaceAll(marker, '');
    }
    return cleanedText;
}

/**
 * Replaces escaped newlines with actual newlines in a string.
 * @param {string} text The text to process.
 * @returns {string} The text with actual newlines.
 */
export const replaceEscapedNewlines = (text) => {
    return text.replace(/\\n/g, '<br />');
}


/**
 * Extracts the first balanced JSON object string from a text.
 * It finds the first '{' and counts until the braces are balanced.
 * @param {string} text The text to search within.
 * @returns {string|null} The extracted JSON string or null if not found.
 */
function extractJson(text) {
    let depth = 0;
    let start = -1;

    for (let i = 0; i < text.length; i++) {
        if (text[i] === '{') {
            if (depth === 0) {
                start = i;
            }
            depth++;
        } else if (text[i] === '}') {
            if (depth > 0) {
                depth--;
                if (depth === 0 && start !== -1) {
                    return text.substring(start, i + 1);
                }
            }
        }
    }
    return null;
}

/**
 * Parses text content to find and validate JSON segments
 * @param {string} text - The text content to parse
 * @returns {Object} Parsed content with type and data/segments
 */
export const parseMessageContent = (text) => {
    const validJsonSegments = [];
    let lastIndex = 0;
    let hasValidJson = false;
    let remainingText = text;

    while (remainingText.length > 0) {
        const jsonString = extractJson(remainingText);

        if (jsonString === null) {
            // No more JSON, add remaining text as markdown
            if (lastIndex < text.length) {
                const cleanText = removeCodeSyntax(text.substring(lastIndex));
                if(cleanText.trim()){
                    validJsonSegments.push(new ContentSegment({
                        type: AiResponseContentType.MARKDOWN,
                        data: cleanText,
                    }));
                }
            }
            break;
        }

        const jsonStartIndexInFullText = text.indexOf(jsonString, lastIndex);
        
        try {
            const parsedContent = JSON.parse(jsonString);

            if (parsedContent && typeof parsedContent === 'object' && parsedContent.dataType) {
                hasValidJson = true;
                // Add preceding text as markdown
                if (jsonStartIndexInFullText > lastIndex) {
                    const markdownPart = text.substring(lastIndex, jsonStartIndexInFullText);
                    const cleanText = removeCodeSyntax(markdownPart);
                    if (cleanText.trim()) {
                        validJsonSegments.push(new ContentSegment({
                            type: AiResponseContentType.MARKDOWN,
                            data: cleanText
                        }));
                    }
                }
                
                // Add the JSON segment
                validJsonSegments.push(new ContentSegment({
                    type: parsedContent.dataType,
                    data: parsedContent.data,
                    title: parsedContent.title || null
                }));
                lastIndex = jsonStartIndexInFullText + jsonString.length;

            } else {
                 // It's valid JSON, but not our format. Treat as markdown.
                 // We find the next potential starting point by advancing past the found JSON
                const endOfThisJsonInFullText = jsonStartIndexInFullText + jsonString.length;
                const markdownPart = text.substring(lastIndex, endOfThisJsonInFullText);
                const cleanText = removeCodeSyntax(markdownPart);
                 if (cleanText.trim()) {
                    validJsonSegments.push(new ContentSegment({
                        type: AiResponseContentType.MARKDOWN,
                        data: cleanText
                    }));
                }
                lastIndex = endOfThisJsonInFullText;
            }
        } catch (e) {
            // JSON parsing failed. Treat this chunk as markdown and continue.
            const endOfThisJsonInFullText = jsonStartIndexInFullText + jsonString.length;
            const markdownPart = text.substring(lastIndex, endOfThisJsonInFullText);
            const cleanText = removeCodeSyntax(markdownPart);
             if (cleanText.trim()) {
                validJsonSegments.push(new ContentSegment({
                    type: AiResponseContentType.MARKDOWN,
                    data: cleanText,
                }));
            }
            lastIndex = endOfThisJsonInFullText;
        }
        remainingText = text.substring(lastIndex);
    }
    
    if (!hasValidJson) {
        const cleanText = removeCodeSyntax(text);
        return new ParsedContent({ type: AiResponseContentType.MARKDOWN, data: cleanText });
    }


    return new ParsedContent({
        type: AiResponseContentType.MULTI,
        segments: validJsonSegments,
    });
};

